import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  Button,
  Tag,
  Spin
} from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";

function MockInterviewStudent() {

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInterviews = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/mock-interview/get-student-interviews`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setInterviews(res.data.data);
      }

      setLoading(false);

    } catch (err) {
      setLoading(false);
      toast.error("Failed to load interviews");
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, []);

  const getStatusTag = (status) => {
    switch (status) {
      case "SCHEDULED":
        return <Tag color="blue" className="p-1 font-bold">Scheduled</Tag>;
      case "COMPLETED":
        return <Tag color="green" className="p-1 font-bold">Completed</Tag>;
      case "IN_PROGRESS":
        return <Tag color="cyan" className="p-1 font-bold">In Progress</Tag>;
        case "RESCHEDULED":
        return <Tag color="orange" className="p-1 font-bold">Rescheduled</Tag>;
      case "NO_SHOW":
        return <Tag color="red" className="p-1 font-bold">NO_SHOW</Tag>;
      default:
        return <Tag>Unknown</Tag>;
    }
  };

  return (
    <div className="min-h-screen p-10 bg-slate-50">

       <ToastContainer
                          position="bottom-left"
                          autoClose={5000}
                          hideProgressBar={true}
                          newestOnTop={false}
                          closeOnClick
                          rtl={false}
                          pauseOnFocusLoss
                          draggable
                          pauseOnHover
                          theme="dark"
                          className="!mb-6 !ml-4"
                          toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
                        />

      <div className="flex flex-col items-start justify-between gap-3 mb-10 md:flex-row md:items-center">

  <div>
    <h1 className="text-4xl font-extrabold tracking-tight text-slate-800">
      Mock Interviews
    </h1>

    <p className="mt-2 text-base text-slate-800">
      Prepare for real-world technical interviews with structured mock sessions. 
      Track your scheduled interviews, evaluate performance through feedback and scores, 
      and continuously improve your chances of getting hired.
    </p>
  </div>

</div>

      {loading ? (
        <div className="flex justify-center mt-20">
          <Spin size="large" />
        </div>
      ) : interviews.length === 0 ? (

        /* 🔥 FANCY EMPTY STATE */
        <div className="flex flex-col items-center justify-center mt-20 text-center">

          <div className="max-w-xl p-10 bg-white border shadow-xl rounded-2xl">

            <h2 className="mb-4 text-2xl font-bold text-slate-800">
              No Mock Interviews Yet 🚀
            </h2>

            <p className="mb-6 text-slate-600">
              Start your journey by scheduling a mock interview.
            </p>

            <div className="p-4 mb-6 text-sm text-left rounded-lg bg-slate-100 text-slate-700">
              <p className="mb-2 font-semibold">📌 Steps:</p>
              <ul className="ml-5 space-y-1 list-disc">
                <li>Go to <b>My Courses</b></li>
                <li>Select your course</li>
                <li>Choose available slots</li>
                <li>Book interview</li>
              </ul>
            </div>

            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/academy/mentee-dashboard/my-courses")}
            >
              Go to My Courses
            </Button>

          </div>

        </div>

      ) : (

     <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">

  {interviews.map((item) => (

    <Card
      key={item.id}
      onClick={() =>
        navigate(`/academy/mentee-dashboard/mock-interviews/${item.id}/details`)
      }
      className="relative p-5 transition-all duration-300 border-black rounded-lg shadow-sm cursor-pointer border-1 bg-gradient-to-br from-white to-slate-50 hover:shadow-xl hover:-translate-y-1 group"
    >

      {/* TOP SECTION */}
      <div className="flex items-start justify-between mb-4">

        <div>
          <h2 className="text-lg font-bold transition text-slate-800 group-hover:text-indigo-600">
            {item.CourseName}
          </h2>
          <p className="text-sm text-slate-500">
            {item.BatchName} • {item.BatchType}
          </p>
        </div>

        {/* STATUS */}
        <div>
          {getStatusTag(item.InterviewStatus)}
        </div>

      </div>

      {/* INSTRUCTOR */}
      <div className="p-3 mb-4 border border-indigo-100 rounded-xl bg-indigo-50">
        <p className="text-xs font-medium tracking-wide text-indigo-400 uppercase">
          Instructor
        </p>
        <p className="font-semibold text-indigo-700">
          {item.InstructorName}
        </p>
      </div>

      {/* DATE & TIME */}
      <div className="grid grid-cols-2 gap-4 mb-4">

        <div className="p-3 border border-blue-100 rounded-xl bg-blue-50">
          <p className="text-xs text-blue-500">Date</p>
          <p className="font-semibold text-blue-800">
            {dayjs(item.ScheduledDate).format("DD MMM YYYY")}
          </p>
        </div>

        <div className="p-3 border border-purple-100 rounded-xl bg-purple-50">
          <p className="text-xs text-purple-500">Time</p>
          <p className="font-semibold text-purple-800">
            {dayjs(item.ScheduledTime, ["HH:mm", "HH:mm:ss"]).format("hh:mm A")}
          </p>
        </div>

      </div>

      {/* SCORE SECTION */}
      {item.Score && (
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">

          <div className="p-3 rounded-xl bg-indigo-50">
            <p className="text-xs text-indigo-500">Score</p>
            <p className="text-lg font-bold text-indigo-700">
              {item.Score}
            </p>
          </div>

          <div className="p-3 rounded-xl bg-emerald-50">
            <p className="text-xs text-emerald-500">Hiring</p>
            <p className="text-lg font-bold text-emerald-700">
              {item.ProbabilityOfHiring}
            </p>
          </div>

        </div>
      )}

      {/* LEFT ACCENT BAR */}
      <div className="absolute top-0 left-0 w-1 h-full rounded-l-2xl bg-gradient-to-b from-indigo-500 to-purple-600"></div>

      {/* HOVER OVERLAY */}
      <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 opacity-0 rounded-2xl bg-indigo-600/10 group-hover:opacity-100">
        <span className="text-sm font-semibold text-indigo-700">
          View Details →
        </span>
      </div>

    </Card>

  ))}

</div>
      )}

    </div>
  );
}

export default MockInterviewStudent;